import React, { createContext, useEffect, useState } from "react";
import { AxiosRequestConfig, AxiosResponse } from "axios";
import { AuthContextType, AuthProviderProps, resToken } from "./model";
import { useNavigate } from "react-router-dom";
import Cookies from "js-cookie";
import api from "@/api/axios";

const AuthContext = createContext<AuthContextType | null>(null);

export const AuthProvider = ({ children }: AuthProviderProps): JSX.Element => {
  const navigate = useNavigate();

  const [auth, setAuth] = useState<any>({});

  const refreshToken = async (): Promise<any> => {
    try {
      const response = await api.get("/admin/token/refresh", {
        //@ts-ignore
        isRefreshToken: true,
      });
      const newAuth = {
        access_token: response.data.data.access_token,
        refresh_token: response.data.data.refresh_token,
      };

      return newAuth;
    } catch (error) {
      console.error("Failed to refresh token:", error);
      throw error;
    }
  };

  const login = (res: resToken) => {
    setAuth({
      accessToken: res.access_token,
      refreshToken: res.refresh_token,
      fullName: res.fullname,
      email: res.email,
      photo: res.photo,
      role: res.role
    });
    Cookies.set("access_token", res?.access_token, { expires: 100 });
    Cookies.set("refresh_token", res?.refresh_token, { expires: 100 });
    const userInfo = {
      fullName: res.fullname,
      email: res.email,
      photo: res.photo,
      role: res.role
    };
    Cookies.set("user_info", JSON.stringify(userInfo), { expires: 100 });
    navigate("/dashboard", { replace: true });
  };

  const logout = () => {
    setAuth({});
    Cookies.remove("access_token");
    Cookies.remove("refresh_token");
    Cookies.remove("user_info");
    navigate("/", { replace: true });
  };

  const refreshAccessToken = async (): Promise<void> => {
    try {
      const newAccessToken = await refreshToken();
      setAuth(newAccessToken);
      Cookies.set("access_token", newAccessToken?.access_token, {
        expires: 100,
      });
      Cookies.set("refresh_token", newAccessToken?.refresh_token, {
        expires: 100,
      });
    } catch (error) {
      console.error("Failed to refresh access token:", error);
    }
  };

  //@ts-ignore
  api.interceptors.request.use((config: AxiosRequestConfig) => {
    config.headers = config.headers ?? {};
    const accessToken = Cookies.get("access_token");
    const refreshToken = Cookies.get("refresh_token");
    //@ts-ignore
    if (config.isRefreshToken) {
      config.headers["Authorization"] = `Bearer ${refreshToken}`;
    } else if (accessToken) {
      config.headers["Authorization"] = `Bearer ${accessToken}`;
    }
    return config;
  });

  api.interceptors.response.use(
    (response: AxiosResponse) => response,
    async (error) => {
      const originalRequest = error.config;

      if (error.response.status === 401 && !originalRequest._retry) {
        originalRequest._retry = true;

        try {
          await refreshAccessToken();
          return api(originalRequest);
        } catch (refreshError) {
          return Promise.reject(refreshError);
        }
      }

      if (
        error.response.status === 404 &&
        error.response.message === "user not found"
      ) {
        logout();
      }

      return Promise.reject(error);
    }
  );

  useEffect(() => {
    const storedAccessToken = Cookies.get("access_token");
    const storedRefreshToken = Cookies.get("refresh_token");
    const storedUserInfo = Cookies.get("user_info");
    const parsedUserInfo = storedUserInfo ? JSON.parse(storedUserInfo) : null;

    if (storedAccessToken && storedRefreshToken) {
      setAuth({
        accessToken: storedAccessToken,
        refreshToken: storedRefreshToken,
        fullName: parsedUserInfo?.fullName || "",
        email: parsedUserInfo?.email || "",
        photo: parsedUserInfo?.photo || "",
        role: parsedUserInfo?.role || "",
      });
    }
  }, []);

  const authContextValue: AuthContextType = {
    api,
    refreshAccessToken,
    auth,
    setAuth,
    login,
    logout,
  };

  return (
    <AuthContext.Provider value={authContextValue}>
      {children}
    </AuthContext.Provider>
  );
};

export default AuthContext;
